import React from "react";
import { Box, Typography, Button, Container, styled, keyframes, IconButton } from "@mui/material";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Social Icons
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import YouTubeIcon from "@mui/icons-material/YouTube";

// --- Animations ---

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
  40% {transform: translateY(-10px);}
  60% {transform: translateY(-5px);}
`;

// --- Styled Components ---

// --- Updated Styled Components ---

const HeroSection = styled(Box)(({ theme }) => {
    // Access the global theme state
    const isSkyMode = theme.palette.background.default === "#BBE0EF";

    return {
        width: "100%",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        borderBottom: `8px solid ${isSkyMode ? "#BBE0EF" : "#09122C"}`,

        // Dynamic Background: If SkyMode is on, we use a lighter overlay
        background: isSkyMode
            ? `linear-gradient(rgba(187, 224, 239, 0.8), rgba(187, 224, 239, 0.9)), 
               url("https://images.unsplash.com/photo-1571260899304-425eee4c7efc?q=80&w=1170&auto=format&fit=crop")`
            : `linear-gradient(rgba(9, 18, 44, 0.7), rgba(9, 18, 44, 0.8)), 
               url("https://images.unsplash.com/photo-1571260899304-425eee4c7efc?q=80&w=1170&auto=format&fit=crop")`,

        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        transition: "all 0.5s ease", // Smooth global transition

        [theme.breakpoints.down("md")]: {
            backgroundAttachment: "scroll",
        },
    };
});

const HeroTitle = styled(Typography)(({ theme }) => ({
    // Switch title color to Navy if the background is Sky Blue for readability
    color: theme.palette.background.default === "#BBE0EF" ? "#09122C" : "#FFFFFF",
    fontWeight: 900,
    fontSize: "3.8rem",
    letterSpacing: "-0.04em",
    lineHeight: 1.1,
    marginBottom: theme.spacing(2),
    transition: "color 0.5s ease",
    [theme.breakpoints.down("sm")]: {
        fontSize: "2.4rem",
    },
}));

const HeroSubtitle = styled(Typography)(({ theme }) => {
    const isSkyMode = theme.palette.background.default === "#BBE0EF";
    return {
        color: isSkyMode ? "#09122C" : "#BBE0EF",
        fontWeight: 600,
        fontSize: "1.2rem",
        maxWidth: "600px",
        marginBottom: theme.spacing(5),
        lineHeight: 1.6,
        opacity: isSkyMode ? 0.8 : 1,
        transition: "color 0.5s ease",
        [theme.breakpoints.down("sm")]: {
            fontSize: "1rem",
        },
    };
});

const ActionButton = styled(Button)(({ theme }) => {
    const isSkyMode = theme.palette.background.default === "#BBE0EF";
    return {
        width: "fit-content",
        minWidth: "180px",
        // Button becomes Navy when background is Sky Blue
        backgroundColor: isSkyMode ? "#09122C" : "#FFFFFF",
        color: isSkyMode ? "#FFFFFF" : "#09122C",
        fontWeight: 700,
        textTransform: "none",
        padding: "14px 40px",
        borderRadius: "50px",
        fontSize: "1.05rem",
        transition: "all 0.3s ease",
        "&:hover": {
            backgroundColor: isSkyMode ? "#1a2a5a" : "#BBE0EF",
            transform: "translateY(-3px)",
            boxShadow: isSkyMode
                ? "0 10px 20px rgba(0,0,0,0.2)"
                : "0 10px 25px rgba(187, 224, 239, 0.4)",
        },
    };
});

// Use this same logic for SocialIconBtn and ScrollIndicator...




const TopLeftSocials = styled(Box)(({ theme }) => ({
    position: "absolute",
    top: "20px",
    left: "20px",
    zIndex: 10,
    display: "flex",
    [theme.breakpoints.down("sm")]: {
        top: "10px",
        left: "10px",
    },
}));

const SocialIconBtn = styled(IconButton)(({ theme }) => ({
    display: "inline-block",
    color: "rgba(255, 255, 255, 0.8)",
    transition: "all 0.3s ease",
    padding: "8px",
    "& svg": {
        fontSize: "1.5rem",
        [theme.breakpoints.down("sm")]: {
            fontSize: "1.2rem",
        },
    },
    "&:hover": {
        color: "#BBE0EF", // Updated to Sky Blue
        transform: "translateY(-3px)",
        filter: "drop-shadow(0px 0px 8px #BBE0EF)",
    },
}));

const ContentWrapper = styled(motion.div)({
    textAlign: "center",
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
});


const ScrollIndicator = styled(Box)({
    position: "absolute",
    bottom: "30px",
    left: "50%",
    transform: "translateX(-50%)",
    color: "#BBE0EF", // Updated to Sky Blue
    textAlign: "center",
    opacity: 0.9,
    animation: `${bounce} 2s infinite`,
    cursor: "pointer",
});

export default function Hero() {
    return (
        <HeroSection>
            <TopLeftSocials>
                <SocialIconBtn aria-label="Facebook">
                    <FacebookIcon />
                </SocialIconBtn>
                <SocialIconBtn aria-label="Instagram">
                    <InstagramIcon />
                </SocialIconBtn>
                <SocialIconBtn aria-label="LinkedIn">
                    <LinkedInIcon />
                </SocialIconBtn>
                <SocialIconBtn aria-label="YouTube">
                    <YouTubeIcon />
                </SocialIconBtn>
            </TopLeftSocials>

            <Container maxWidth="lg">
                <ContentWrapper
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    <HeroTitle variant="h1">
                        CRYSTAL CAREER CENTER
                    </HeroTitle>

                    <HeroSubtitle>
                        Empowering your professional journey through strategic career
                        planning and elite university partnerships.
                    </HeroSubtitle>

                    <Link to="/login" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained" disableElevation>
                            Get Started
                        </ActionButton>
                    </Link>
                </ContentWrapper>
            </Container>

            <ScrollIndicator>
                <Typography variant="caption" sx={{ display: "block", letterSpacing: 3, mb: 0.5, fontWeight: 700 }}>
                    SCROLL
                </Typography>
                <ExpandMoreIcon fontSize="large" />
            </ScrollIndicator>
        </HeroSection>
    );
}
